import React, { useEffect, useContext } from 'react';
import { Grid, Card, CardContent, CircularProgress, Typography, Box, Button } from '@mui/material';

import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BusinessIcon from '@mui/icons-material/Business';
import ForumIcon from '@mui/icons-material/Forum';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import InventoryIcon from '@mui/icons-material/Inventory';
import BuildIcon from '@mui/icons-material/Build';

import { AuthenticationContext } from './auth/contexts/AuthenticationContext';

const IntegrationsPage = () => {
  const { user, fetchUser } = useContext(AuthenticationContext);

  const hasSalesEstimateModule = user?.modules?.some((module) => module.value === 'sales_estimate');
  const hasChillerLogModule = user?.modules?.some((module) => module.value === 'chiller_logs');
  const hasChillerLogAdminModule = user?.modules?.some(
    (module) => module.value === 'chiller_logs' && parseInt(module.tier) > 2,
  );
  const hasEnergySolutionsModule = user?.modules?.some((module) => module.value === 'energy_solutions');
  const hasBackflowTestingModule = user?.modules?.some((module) => module.value === 'backflow_testing');
  const hasBackflowTestingAdminModule = user?.modules?.some(
    (module) => module.value === 'backflow_testing' && parseInt(module.tier) > 2,
  );
  const hasCustomerIssuesModule = user?.modules?.some((module) => module.value === 'customer_issues');
  const hasVendorManagementModule = user?.modules?.some((module) => module.value === 'vendor_statements');
  const hasProjectCommunicationModule = user?.modules?.some((module) => module.value === 'project_communication');
  const hasHomeServicesModule = user?.modules?.some((module) => module.value === 'home_services');
  const hasEmployeePortalModule = user?.modules?.some((module) => module.value === 'employee_portal');
  const hasAdminModule = user?.modules?.some((module) => module.value === 'admin' && parseInt(module.tier) > 2);
  const hasDeveloperModule = user?.modules?.some((module) => module.value === 'developer');

  const [loaded, setLoaded] = React.useState(false);
  const [previousPermissions, setPreviousPermissions] = React.useState(null);
  const [userDataFetched, setUserDataFetched] = React.useState(false);

  const checkPermissionsAndRedirect = React.useCallback(() => {
    const count =
      (hasSalesEstimateModule ? 1 : 0) +
      (hasChillerLogModule ? 1 : 0) +
      (hasEnergySolutionsModule ? 1 : 0) +
      (hasBackflowTestingModule ? 1 : 0) +
      (hasCustomerIssuesModule ? 1 : 0) +
      (hasVendorManagementModule ? 1 : 0) +
      (hasProjectCommunicationModule ? 1 : 0) +
      (hasHomeServicesModule ? 1 : 0) +
      (hasEmployeePortalModule ? 1 : 0) +
      (hasAdminModule ? 1 : 0);
    const currentPermissions = {
      hasSalesEstimateModule,
      hasChillerLogModule,
      hasEnergySolutionsModule,
      hasBackflowTestingModule,
      hasCustomerIssuesModule,
      hasVendorManagementModule,
      hasProjectCommunicationModule,
      hasHomeServicesModule,
      hasEmployeePortalModule,
      hasAdminModule,
      hasDeveloperModule,
    };

    // Only update UI if permissions have actually changed
    if (JSON.stringify(previousPermissions) !== JSON.stringify(currentPermissions)) {
      setPreviousPermissions(currentPermissions);

      if (count === 1) {
        setLoaded(false);
        if (hasSalesEstimateModule) {
          window.location.href = '/rooftop_automation/recent';
        } else if (hasChillerLogModule) {
          window.location.href = '/chiller_logs/search';
        } else if (hasEnergySolutionsModule) {
          window.location.href = '/energy_solutions/customer_lookup';
        } else if (hasBackflowTestingModule) {
          window.location.href = '/backflow_tests/search';
        } else if (hasCustomerIssuesModule) {
          window.location.href = '/customer_issues/search';
        } else if (hasVendorManagementModule) {
          window.location.href = '/vendors/invoices';
        } else if (hasProjectCommunicationModule) {
          window.location.href = '/project_communications/search';
        } else if (hasHomeServicesModule) {
          window.location.href = '/home_services/upcoming_inspections';
        } else if (hasEmployeePortalModule) {
          window.location.href = '/employee_portal/warehouse_pickup';
        } else if (hasAdminModule) {
          window.location.href = '/admin/user_management';
        }
      } else {
        setLoaded(true);
      }
    }
  }, [
    hasSalesEstimateModule,
    hasChillerLogModule,
    hasEnergySolutionsModule,
    hasBackflowTestingModule,
    hasCustomerIssuesModule,
    hasVendorManagementModule,
    hasProjectCommunicationModule,
    hasHomeServicesModule,
    hasEmployeePortalModule,
    hasAdminModule,
    previousPermissions,
    hasDeveloperModule,
  ]);

  useEffect(() => {
    checkPermissionsAndRedirect();
  }, [checkPermissionsAndRedirect]);

  useEffect(() => {
    // Set up interval to fetch user data every second (no need to call checkPermissionsAndRedirect here)
    const interval = setInterval(() => {
      if (fetchUser) {
        fetchUser(); // fetchUser will only update state if data changed, triggering re-render
        setUserDataFetched(true); // Mark that we've fetched user data at least once
      }
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [fetchUser]);

  const handleChillerLogClick = () => {
    if (hasChillerLogAdminModule) {
      window.location.href = '/chiller_logs/search';
    } else {
      window.location.href = '/chiller_logs/search';
    }
  };

  const handleDeveloperClick = () => {
    window.location.href = '/developers/servman_sql_logs';
  };

  const handleEnergySolutionsClick = () => {
    window.location.href = '/energy_solutions/customer_lookup';
  };

  const handleBackflowTestingClick = () => {
    if (hasBackflowTestingAdminModule) {
      window.location.href = '/backflow_tests/search';
    } else {
      window.location.href = '/backflow_tests/search';
    }
  };

  const handleCustomerIssuesClick = () => {
    window.location.href = '/customer_issues/search';
  };

  const handleVendorManagementClick = () => {
    window.location.href = '/vendors/invoices';
  };

  const handleProjectCommunicationClick = () => {
    window.location.href = '/project_communications/search';
  };

  const handleAdminClick = () => {
    window.location.href = '/admin/user_management';
  };

  const allIntegrations = [
    {
      name: 'Admin',
      description: 'Manage users, permissions, and system settings.',
      connected: hasAdminModule,
      icon: <AdminPanelSettingsIcon />,
      onClick: hasAdminModule ? handleAdminClick : () => null,
    },
    {
      name: 'Backflow Testing',
      description: 'Manage backflow prevention device testing, approvals, and water authority submissions.',
      connected: hasBackflowTestingModule,
      icon: <WaterDropIcon />,
      onClick: hasBackflowTestingModule ? handleBackflowTestingClick : () => null,
    },
    {
      name: 'Chiller Logs',
      description: 'Streamline the process of collecting, approving, and sharing HVAC unit condition logs',
      connected: hasChillerLogModule,
      icon: <FactCheckIcon />,
      onClick: hasChillerLogModule ? handleChillerLogClick : () => null,
    },
    {
      name: 'Customer Issues',
      description: 'Track and manage customer issues from ServiceMan calendar system.',
      connected: hasCustomerIssuesModule,
      icon: <AssignmentLateIcon />,
      onClick: hasCustomerIssuesModule ? handleCustomerIssuesClick : () => null,
    },
    {
      name: 'Developers',
      description: 'Debugging Tools for the Site Developer Team.',
      connected: hasDeveloperModule,
      icon: <BuildIcon />,
      onClick: hasDeveloperModule ? handleDeveloperClick : () => null,
    },
    {
      name: 'Energy Solutions',
      description: 'Compare Energy Ratings of assets for a specific customer.',
      connected: hasEnergySolutionsModule,
      icon: <EnergySavingsLeafIcon />,
      onClick: hasEnergySolutionsModule ? handleEnergySolutionsClick : () => null,
    },
    {
      name: 'Home Inspections',
      description: 'Complete HVAC, Plumbing, Electrical and Water Treatment inspection reports for home services.',
      connected: hasHomeServicesModule,
      icon: <HomeRepairServiceIcon />,
      onClick: hasHomeServicesModule
        ? () => (window.location.href = '/home_services/upcoming_inspections')
        : () => null,
    },
    {
      name: 'Project Communication',
      description: 'Review and update project communication details from Servman.',
      connected: hasProjectCommunicationModule,
      icon: <ForumIcon />,
      onClick: hasProjectCommunicationModule ? handleProjectCommunicationClick : () => null,
    },
    {
      name: 'Sales Estimates',
      description: 'Create and track project sales estimates and replacement equipment for rooftop unit projects.',
      connected: hasSalesEstimateModule,
      icon: <MonetizationOnIcon />,
      onClick: hasSalesEstimateModule ? () => (window.location.href = '/rooftop_automation/recent') : () => null,
    },
    {
      name: 'Vendor Statements',
      description: 'Vendor invoice processing and discrepancy tracking.',
      connected: hasVendorManagementModule,
      icon: <BusinessIcon />,
      onClick: hasVendorManagementModule ? handleVendorManagementClick : () => null,
    },
    {
      name: 'Employee Portal',
      description: 'Warehouse pickup and purchase order management for warehouse staff.',
      connected: hasEmployeePortalModule,
      icon: <InventoryIcon />,
      onClick: hasEmployeePortalModule
        ? () => (window.location.href = '/employee_portal/warehouse_pickup')
        : () => null,
    },
  ];

  // Filter to only show integrations the user has access to
  const integrations = allIntegrations.filter((integration) => integration.connected);

  return !loaded || !userDataFetched ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  ) : (
    <Box padding={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h1" fontWeight="bold">
          TUDI Integrations
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {integrations.map((integration, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card variant="outlined" style={{ height: 200, display: 'flex', flexDirection: 'column' }}>
              <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  {integration.icon}
                  <Typography variant="h6" fontWeight="bold" style={{ marginLeft: 10 }}>
                    {integration.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ flex: 1, marginBottom: 16 }}>
                  {integration.description}
                </Typography>
                <Button variant="contained" color="primary" fullWidth onClick={integration.onClick}>
                  Open {integration.name}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default IntegrationsPage;
